import { useFeatureGate } from "@statsig/react-bindings";
import { useCallback } from "react";
import { useLocalStorage } from "usehooks-ts";
import {
  AppConfig,
  COERCED_VIDEO_PLATFORM_KEY,
  DEV_APP_SETTINGS_KEY,
  getDevAppSettings,
  IS_DEV_APP_SETTINGS_ENABLED_KEY,
} from "../config";
import { enableZoomGate } from "../statsig/gates";
import { Statsig } from "../statsig/StatsigProvider";
import { VideoPlatform } from "../video-sdk";
import { VIDEO_PLATFORMS } from "../video-sdk/core/constants";
import { useCoercedVideoPlatform } from "./useCoercedVideoPlatform";

/**
 * Determines if Zoom video integration is enabled for the current user.
 * This hook implements a provider-dependent feature pattern where patient access
 * to Zoom functionality depends on their provider's Statsig allocation.
 *
 * Uses a feature gate with "providerShortID" (case-sensitive) as the
 * Statsig ID Type, defined as a customID on the StatsigUser.
 * @see gracefullyUpdateStatsigUser for user configuration details
 */
export function useIsZoomEnabled(): boolean {
  const [coercedVideoPlatform] = useCoercedVideoPlatform();
  const { value: isZoomGateEnabled } = useFeatureGate(enableZoomGate, {
    disableExposureLog: !!coercedVideoPlatform,
  });
  const [isDevAppSettingsEnabled] = useLocalStorage<number>(
    IS_DEV_APP_SETTINGS_ENABLED_KEY,
    0,
  );
  const [devAppSettings] = useLocalStorage<Partial<AppConfig>>(
    DEV_APP_SETTINGS_KEY,
    {},
  );

  const hasDevVideoPlatformOverride =
    isDevAppSettingsEnabled &&
    VIDEO_PLATFORMS.has(devAppSettings?.videoPlatformOverride as VideoPlatform);

  let isZoomEnabled: boolean;
  if (coercedVideoPlatform) {
    // if coerced video platform is set (for mismatch remediation), use it
    isZoomEnabled = coercedVideoPlatform === VideoPlatform.Zoom;
  } else if (hasDevVideoPlatformOverride) {
    // if dev video platform override is set, use it
    isZoomEnabled =
      devAppSettings?.videoPlatformOverride === VideoPlatform.Zoom;
  } else {
    // otherwise, use statsig gate
    isZoomEnabled = isZoomGateEnabled;
  }

  return isZoomEnabled;
}

export function useIsZoomEnabledCallback(): () => boolean {
  return useCallback(() => {
    const devAppSettings = getDevAppSettings();
    const clientVideoPlatform = localStorage.getItem(
      COERCED_VIDEO_PLATFORM_KEY,
    );
    const coercedVideoPlatform =
      clientVideoPlatform &&
      VIDEO_PLATFORMS.has(JSON.parse(clientVideoPlatform) as VideoPlatform)
        ? (JSON.parse(clientVideoPlatform) as VideoPlatform)
        : null;
    const hasDevVideoPlatformOverride =
      localStorage.getItem(IS_DEV_APP_SETTINGS_ENABLED_KEY) === "1" &&
      VIDEO_PLATFORMS.has(
        devAppSettings?.videoPlatformOverride as VideoPlatform,
      );

    let isZoomEnabled: boolean;
    if (coercedVideoPlatform) {
      // if coerced video platform is set (for mismatch remediation), use it
      isZoomEnabled = coercedVideoPlatform === VideoPlatform.Zoom;
    } else if (hasDevVideoPlatformOverride) {
      // if dev video platform override is set, use it
      isZoomEnabled =
        devAppSettings?.videoPlatformOverride === VideoPlatform.Zoom;
    } else {
      // otherwise, use statsig gate
      isZoomEnabled = Statsig.checkGate(enableZoomGate, {
        disableExposureLog: !!coercedVideoPlatform,
      });
    }

    return isZoomEnabled;
  }, []);
}
